import React, { useState, forwardRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import LoadingDots from '../LoadingDots/LoadingDots';
import SkeletonLoaderWrapper from '../Loaders/SkeletonLoaderWrapper';
import { getStarIcon } from '../../utils/starRating';

// 🔄 SVGs moved to Cloudflare R2
const Feefo =
  'https://assets.askhippo.co.za/Pages/Rating%20Providers/feefo.svg';
const Google =
  'https://assets.askhippo.co.za/Pages/Rating%20Providers/google.svg';
const HelloPeter =
  'https://assets.askhippo.co.za/Pages/Rating%20Providers/hellopeter.svg';
const BuyIcon = 'https://assets.askhippo.co.za/Icons/common/Union.svg';

const MobileProviderTileComponent = forwardRef(
  (
    {
      data,
      initialLoadDone,
      onSelectQuote,
      onBuyOnline,
      bestDealContentReady = true,
      industryCategory,
    },
    ref
  ) => {
    const { FriendlyName, LogoURL } = data;
    const bestDealBannerText = industryCategory
      ? `Recommended for ${industryCategory}`
      : 'Recommended for Your Business';

    const reviewData = [
      {
        name: 'Feefo',
        score: parseFloat(
          (data.FeefoScore ?? data.FeefoStars ?? '0')
            .toString()
            .replace(',', '.')
        ),
        logo: Feefo,
      },
      {
        name: 'Google',
        score: parseFloat(
          (data.GoogleScore ?? data.GoogleStars ?? '0')
            .toString()
            .replace(',', '.')
        ),
        logo: Google,
      },
      {
        name: 'HelloPeter',
        score: parseFloat(
          (data.HelloPeterScore ?? data.HelloPeterStars ?? '0')
            .toString()
            .replace(',', '.')
        ),
        logo: HelloPeter,
      },
    ];

    const [isExpanded, setIsExpanded] = useState(false);

    const isBestDealLoading = data.bestDeal && !bestDealContentReady;

    const brandName = (data.BrandName ?? data.FriendlyName ?? '').toString();
    const brandNameClean = brandName.replace('.co.za', '');

    // Align with desktop: use BrandText as the “subtitle” line (not ProductName)
    const subtitleText = data.BrandText ?? data.ProductName ?? '';

    const hasRetailChips =
      initialLoadDone &&
      Array.isArray(data.RetailChips) &&
      data.RetailChips.length > 0;

    const handleSelectQuote = (evt) => {
      onSelectQuote(evt, { action: 'select-quote', data });
    };

    const handleBuyOnline = (evt) => {
      return onBuyOnline(evt, { action: 'buy-online', data });
    };

    return (
      <Box
        ref={ref}
        sx={{
          width: { xs: 'calc(100% - 32px)', sm: '100%' },
          margin: { xs: '0 16px 16px 16px', sm: '0 0 16px 0' },
          borderRadius: '22px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 5px 15px 0px #00000026',
          border:
            data.bestDeal && initialLoadDone ? '1px solid #2DC8CB' : 'none',
          boxSizing: 'border-box',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          padding: '16px',
          position: 'relative',
        }}
      >
        {data.bestDeal && initialLoadDone && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '40px',
              backgroundColor: '#2DC8CB',
              borderTopLeftRadius: '18px',
              borderTopRightRadius: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              zIndex: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: '1rem',
                lineHeight: '1.5rem',
                letterSpacing: '-0.02em',
                color: '#FFFFFF',
                textAlign: 'center',
              }}
            >
              {bestDealContentReady ? (
                bestDealBannerText
              ) : (
                <>
                  Calculating Your Best Deal <LoadingDots variant="buy-now" />
                </>
              )}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            padding: '16px',
            width: '100%',
            boxSizing: 'border-box',
            mt: data.bestDeal && initialLoadDone ? '40px' : 0,
          }}
        >
          {/* Expand/Collapse Button */}
          <Button
            onClick={() => setIsExpanded((prev) => !prev)}
            sx={{
              position: 'absolute',
              top: `${data.bestDeal && initialLoadDone ? 71 : 20}px`,
              right: '16px',
              width: '48px',
              height: '48px',
              padding: '8px',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'transparent',
              boxShadow: 'none',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <span
              className="material-symbols-rounded"
              style={{
                fontWeight: 500,
                fontSize: '1.5rem',
                color: '#000',
                transform: isExpanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.3s ease',
                display: 'inline-block',
              }}
            >
              keyboard_arrow_down
            </span>
          </Button>

          {/* Logo */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'left',
            }}
          >
            <SkeletonLoaderWrapper loading={isBestDealLoading}>
              <img
                src={LogoURL}
                alt={FriendlyName ?? brandNameClean}
                style={{
                  maxHeight: '48px',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </SkeletonLoaderWrapper>
          </Box>

          {/* Provider Name */}
          <SkeletonLoaderWrapper loading={isBestDealLoading}>
            <Typography
              sx={{
                mt: '12px',
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: '1rem',
                color: '#1E354E',
              }}
            >
              {brandNameClean}
            </Typography>
          </SkeletonLoaderWrapper>

          {/* Subtitle (align with desktop: BrandText) */}
          <SkeletonLoaderWrapper loading={isBestDealLoading}>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '0.875rem',
                color: '#1E354E',
              }}
            >
              {subtitleText}
            </Typography>
          </SkeletonLoaderWrapper>

          {/* Divider */}
          <Box
            sx={{
              mt: '12px',
              width: '100%',
              height: '1px',
              backgroundColor: '#E0E0E0',
            }}
          />

          {/* Bottom Row: Premium + CTA */}
          <Box
            sx={{
              mt: hasRetailChips ? '8px' : '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              width: '100%',
            }}
          >
            {/* Reviews */}
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 700,
                  fontSize: '1rem',
                  mb: '4px',
                  color: '#1E354E',
                }}
              >
                Reviews
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {reviewData.map((review, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <img
                      src={review.logo}
                      alt={`${review.name} logo`}
                      style={{
                        width: '40px',
                        height: '40px',
                        objectFit: 'contain',
                      }}
                    />

                    <Box sx={{ display: 'flex' }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <img
                          key={star}
                          src={getStarIcon(star, review.score)}
                          alt="star"
                          style={{ width: '18px', height: '18px' }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
            {/* CTA */}
            <Button
              onClick={
                data.OnlinePremium && data.OnlinePremium < data.TotalPremium
                  ? handleBuyOnline
                  : handleSelectQuote
              }
              sx={{
                height: '40px',
                backgroundColor: '#FF0074',
                borderRadius: '18px',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: '16px',
                py: '8px',
                '&:hover': { backgroundColor: '#e60067' },
                ...(isBestDealLoading && {
                  backgroundColor: '#D5D5D5 !important',
                  borderColor: '#D5D5D5 !important',
                  color: '#7A7A7A !important',
                  cursor: 'not-allowed',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#D5D5D5 !important' },
                }),
              }}
            >
              {initialLoadDone &&
              data.OnlinePremium &&
              data.OnlinePremium < data.TotalPremium ? (
                <>
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: '#FFFFFF',
                      mr: '4px',
                    }}
                  >
                    Buy
                  </Typography>
                  <Box
                    component="img"
                    src={BuyIcon}
                    alt="BuyOnline"
                    sx={{
                      mx: '4px',
                      mb: '2px',
                      width: '18px',
                      height: '18px',
                    }}
                  />
                </>
              ) : (
                <span
                  className="material-symbols-rounded"
                  style={{ fontSize: '1.5rem', color: '#FFFFFF' }}
                >
                  double_arrow
                </span>
              )}
            </Button>
          </Box>

          {/* Expanded content aligned to desktop sections */}
          {isExpanded && (
            <Box
              sx={{
                mt: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              {/* Divider */}
              <Box
                sx={{
                  mt: '12px',
                  width: '100%',
                  height: '1px',
                  backgroundColor: '#E0E0E0',
                }}
              />

              {/* Assist Benefits (Desktop: CoverFor) */}
              {data.CoverFor && (
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 700,
                      fontSize: '1rem',
                      mb: '8px',
                      color: '#1E354E',
                    }}
                  >
                    Assist Benefits
                  </Typography>

                  <Box
                    sx={{
                      pl: '12px',
                      mt: '-2px',
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: '0.875rem',
                      lineHeight: '22px',
                      color: '#1E354E',

                      /* Reset incoming html */
                      '& p, & div, & ul': { margin: 0, padding: 0 },
                      '& br': { display: 'none' },

                      /* Remove default bullets */
                      '& li': {
                        listStyle: 'none',
                        position: 'relative',
                        paddingLeft: '22px',
                        marginBottom: '4px',
                      },

                      /* Add tick */
                      '& li::before': {
                        content: '"✓"',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        color: '#1E354E',
                        fontWeight: 700,
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: data.CoverFor ?? '' }}
                  />
                </Box>
              )}

              {/* Cashback (Desktop: CashBack) */}
              {data.CashBack && (
                <>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: 'Inter',
                        fontWeight: 700,
                        fontSize: '1rem',
                        mb: '8px',
                        color: '#1E354E',
                      }}
                    >
                      Cashback
                    </Typography>

                    <Box
                      sx={{
                        pl: '12px',
                        mt: '-2px',
                        fontFamily: 'Inter',
                        fontWeight: 400,
                        fontSize: '0.875rem',
                        lineHeight: '22px',
                        color: '#1E354E',

                        '& p, & div, & ul': { margin: 0, padding: 0 },
                        '& br': { display: 'none' },

                        '& li': {
                          listStyle: 'none',
                          position: 'relative',
                          paddingLeft: '22px',
                          marginBottom: '4px',
                        },

                        '& li::before': {
                          content: '"✓"',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          color: '#1E354E',
                          fontWeight: 700,
                        },
                      }}
                      dangerouslySetInnerHTML={{ __html: data.CashBack ?? '' }}
                    />
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);

MobileProviderTileComponent.displayName = 'MobileProviderTileComponent';
export default MobileProviderTileComponent;
